import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type Owner from '@ember/owner';

interface ItemHash {
  guid: string;
  address: string;
}
export interface EmailInputSignature {
  Element: HTMLInputElement;
  Args: {
    index: number;
    item: ItemHash;
    onChangeValue: (arg0: string, arg1: ItemHash) => void;
    onRemove: (arg0: string) => void;
  };
}
type EmailInputArgs = EmailInputSignature['Args'];

const REGEXP_EMAIL =
  /^(([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5}){1,25})+([;.](([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5}){1,25})+)*$/;

export default class EmailInput extends Component<EmailInputSignature> {
  @tracked itemInEdit: boolean;

  constructor(owner: Owner, args: EmailInputArgs) {
    super(owner, args);
    this.itemInEdit = this.args.item.address.trim() === '';
    // Defer setting the focus to allow the edit box to become visible first.
    // We may want to consider putting this sequence into a modifier in the future.
    setTimeout(() => {
      const element = document.querySelector(`[data-guid="${this.args.item.guid}"]`) as HTMLInputElement;
      if (element) element.select();
    }, 0);
  }

  get itemValueIsInvalid() {
    return this.isAddressInvalid(this.args.item.address);
  }

  // Function that tells us if we are working with an email address, or just garbage.
  isAddressInvalid(value: string) {
    return value.length !== 0 && !REGEXP_EMAIL.test(value);
  }

  // Focus
  @action onFocus(event: UIEvent) {
    const target = event.target as HTMLInputElement;
    target.select();
  }

  // Blur
  @action onBlur(event: UIEvent) {
    const target = event.target as HTMLInputElement;
    const currentValue = target.value.trim();
    this.itemInEdit = currentValue === '';
  }

  // ValueChange
  @action onChangeValue(event: Event) {
    const target = event.target as HTMLInputElement;
    const currentValue = target.value.trim();
    if (currentValue !== this.args.item.address.trim()) {
      // Defer invoking the upstream action to let the event queue clear.
      // Auto-fill generates focus actions we want to settle before making changes to the data that will cause other focus actions.
      // Otherwise, you get weird effects like duplicate auto-fills on the field this fill just caused to be created.
      setTimeout(() => {
        this.args.onChangeValue(this.args.item.guid, {
          address: currentValue,
          guid: this.args.item.guid,
        });
      }, 0);
    }
  }

  // Change Mode, BUTTON ==> EDIT
  @action onOpenForEdit() {
    this.itemInEdit = true;
    // Defer setting the focus to allow the edit box to become visible first.
    setTimeout(() => {
      (document.querySelector(`[data-guid="${this.args.item.guid}"]`) as HTMLInputElement).select();
    }, 0);
  }

  // Clear
  @action onRemove() {
    this.args.onRemove(this.args.item.guid);
  }

  // KeyDown, fires ValueChange on certain key presses.
  @action onKeyDown(event: UIEvent) {
    const keyEvent = event as KeyboardEvent;
    if (keyEvent.key) {
      switch (keyEvent.key.toLowerCase()) {
        case ';': //semicolon
        case ',': //comma
        case 'enter':
          {
            event.preventDefault();
            const target = event.target as HTMLInputElement;
            if (target.value.trim() !== '') {
              this.onChangeValue(event);
              this.itemInEdit = false;
            }
          }
          break;
        default:
          break;
      }
    }
  }
}
